/**
 * Upload affordances shared by every Files list: a button that opens the picker, and a hook
 * that turns any element into a drop target. Both hand back plain `File`s; the module uploads
 * them to its own route (see `uploadFile` in ../lib/files).
 */
import { useRef, useState, type DragEvent, type ReactNode } from 'react';
import { cn } from '../lib/utils';

/** A label-wrapped hidden file input, styled by the caller. */
export function FilePickButton({
  onFiles,
  multiple = true,
  disabled = false,
  className,
  children,
}: {
  onFiles: (files: File[]) => void;
  multiple?: boolean;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <label
      className={cn('cursor-pointer', disabled && 'pointer-events-none opacity-50', className)}
    >
      {children}
      <input
        type="file"
        multiple={multiple}
        disabled={disabled}
        className="hidden"
        onChange={(event) => {
          const picked = Array.from(event.target.files ?? []);
          // Reset, so picking the same file again still fires.
          event.target.value = '';
          if (picked.length > 0) onFiles(picked);
        }}
      />
    </label>
  );
}

/**
 * Drop-target props for an element, and whether files are over it. Only drags carrying files
 * count — a dragged ripple or text selection passes straight through.
 */
export function useFileDrop(onFiles: (files: File[]) => void, enabled = true) {
  const [over, setOver] = useState(false);
  // dragenter/leave fire for every child crossed; count them so the outline doesn't flicker.
  const depth = useRef(0);
  const carriesFiles = (event: DragEvent) =>
    enabled && Array.from(event.dataTransfer.types).includes('Files');

  return {
    over,
    props: {
      onDragEnter: (event: DragEvent) => {
        if (!carriesFiles(event)) return;
        event.preventDefault();
        depth.current += 1;
        setOver(true);
      },
      onDragOver: (event: DragEvent) => {
        if (carriesFiles(event)) event.preventDefault();
      },
      onDragLeave: (event: DragEvent) => {
        if (!carriesFiles(event)) return;
        depth.current = Math.max(0, depth.current - 1);
        if (depth.current === 0) setOver(false);
      },
      onDrop: (event: DragEvent) => {
        if (!carriesFiles(event)) return;
        event.preventDefault();
        depth.current = 0;
        setOver(false);
        const dropped = Array.from(event.dataTransfer.files);
        if (dropped.length > 0) onFiles(dropped);
      },
    },
  };
}
